import React, { useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectModelingSelectedRole, removeGroupRoleChildFromModeling, addGroupRoleChildToModeling } from "../../../../reducers/administration/requestSlice";
import { AppDispatch, RootState } from "../../../../store";
import { integrationService } from "../../../../services/integration-service";
import GroupRoleModelingBox from "../../../reports-modeling/group-role-modeling-box";
import { SelectInput } from "../../../common/form-controls";
import { FormattedMessage, useIntl } from "react-intl";
import { MenuItem } from "../../../common/menu";
import { RequestModelingRole } from "../../../../types/brm/request-types";
import "./styles.less";
import { requestsService } from "../../../../services/requests-service";

type SelectRolesModeProps = {
    value: string;
    setValue: (value: string) => void;
};

const SelectRolesMode: React.FC<SelectRolesModeProps> = ({ value, setValue }) => {
    const intl = useIntl();
    const modes = useMemo(
        () => [
            { key: "allRoles", text: <FormattedMessage id="request-add-roles-to-group-role-dialog-select-mode.all-roles" /> },
            { key: "rolesFromRequest", text: <FormattedMessage id="request-add-roles-to-group-role-dialog-select-mode.roles-from-request" /> },
        ],
        []
    );

    return (
        <SelectInput label={intl.formatMessage({ id: "request-add-roles-to-group-role-dialog-select-mode.label" })} value={value} onChange={(e) => setValue(e.target.value)}>
            {modes.map(({ key, text }) => (
                <MenuItem key={key} value={key} sx={{ minHeight: "initial", height: 36 }}>
                    {text}
                </MenuItem>
            ))}
        </SelectInput>
    );
};

const RequestGroupRoleModelingBox: React.FC<{ disabled: boolean }> = ({ disabled = false }) => {
    const dispatch = useDispatch<AppDispatch>();
    const modelingRole = useSelector(selectModelingSelectedRole);
    const currentRequest = useSelector((state: RootState) => state.administration.requests.currentRequest);
    const [selectedRole, setSelectedRole] = useState("");
    const isDeleteDisabled = disabled ? true : !selectedRole.length;
    const intl = useIntl();

    const [rolesMode, setRolesMode] = useState("allRoles");

    const handleRowClick = useCallback((row: RequestModelingRole, setActiveRow: (id: string) => void) => {
        setActiveRow(row.id);
        setSelectedRole(row.role);
    }, []);

    const handleAddChildRole = useCallback(
        (newChildRole: { description: string; isComplex: boolean; profile: string; role: string; systemId: string; groupId?: string }) => {
            dispatch(addGroupRoleChildToModeling({ newChildRole }));
        },
        [dispatch]
    );

    const handleRemoveChildRoles = useCallback(() => {
        dispatch(removeGroupRoleChildFromModeling({ roleIdToRemove: selectedRole }));
    }, [dispatch, selectedRole]);

    const getRoles = useCallback(
        async (params, signal) => {
            if (rolesMode === "rolesFromRequest") {
                const searchString = params?.search?.toLowerCase() || "";
                const filteredRoles = (currentRequest?.roles || [])
                    .filter((role) => !role.isComplex && role.isNew)
                    .filter((role) => role.role.toLowerCase().includes(searchString));

                const page = params?.page;
                const limit = params?.limit;
                const total = filteredRoles.length;

                return {
                    rows: filteredRoles.slice((page - 1) * limit, page * limit),
                    total,
                };
            }

            const groupId = modelingRole?.groupId;
            const systemId = await requestsService.getTargetSystemId(groupId ?? "");

            if (params && params["filters"]) {
                params["filters"].push({ field: "systemId", sign: "EQ", value: systemId });
            }

            return integrationService.getRoles(params, signal);
        },
        [rolesMode, currentRequest?.roles, modelingRole?.groupId]
    );

    const columns = useMemo(
        () => [
            {
                id: "expand",
                type: "expand",
            },

            {
                id: "role",
                title: intl.formatMessage({ id: "request-detailed-roles-table.group-role-table-column.role" }),
                type: "text",
            },
            {
                id: "groupId",
                title: intl.formatMessage({ id: "request-detailed-roles-table.group-role-table-column.landscape" }),
                type: "text",
            },
            {
                id: "roleType",
                title: intl.formatMessage({ id: "request-detailed-roles-table.group-role-table-column.role-type" }),
                type: "text",
            },
            {
                id: "description",
                title: intl.formatMessage({ id: "request-detailed-roles-table.group-role-table-column.role-description" }),
                type: "text",
            },
        ],
        [intl]
    );
    return (
        <GroupRoleModelingBox
            roles={modelingRole?.children}
            selectedRole={modelingRole}
            onAddChildRole={handleAddChildRole}
            onRemoveChildRoles={handleRemoveChildRoles}
            handleRowClick={handleRowClick}
            handleExpandChildrenRoles={() => {}}
            isAddDisabled={disabled}
            isDeleteDisabled={isDeleteDisabled}
            getRoles={getRoles}
            selectInput={<SelectRolesMode value={rolesMode} setValue={setRolesMode} />}
            columns={columns}
        />
    );
};

export default RequestGroupRoleModelingBox;
